import React, { useState } from "react";
import TabPanel from "./common/TabPanel";
import ParameterTable from "./common/ParameterTable";
import StepGuideModal from "../general/StepGuideModal";
import { useStepGuide } from "../../../hooks/useStepGuide";
import { getStepGuide } from "../../../data/stepGuides";

// Handling advanced parameters
// Daily costs are not included in the list, they are calculated under the hood
export const parameterGroups = {
  penalties: {
    category: "Penalties",
    inputs: [
      { label: "Non ZEV penalty", 
        description: "For drayage operations, the Ports of Los Angeles and Long Beach charge a rate of $10 per 20-foot equivalent container, or $20 per 40-foot equivalent container, for non-exempt trucks", 
        id: "non_ze_penalty_price", 
        defaultValue: 0, 
        help: `Learn more about the penalty <a href='https://www.portoflosangeles.org/environment/air-quality/clean-truck-program' target='_blank' className='text-blue-500 hover:text-blue-800 underline'>here</a>.`,
        unit: "per day"
      },
    ],
  },
  maintenance: {
    category: "Maintenance",
    inputs: [
      { label: "Diesel maintenance", description: "Cost of maintenance for a diesel truck", id: "diesel_maint", defaultValue: 0.21, unit: "$/mile" },
      { label: "Electric maintenance", description: "Cost of maintenance for an electric truck", id: "elec_maint", defaultValue: 0.1, unit: "$/mile" },
    ],
  },
  insurance: {
    category: "Insurance",
    inputs: [
      { label: "Diesel insurance cost", description: "Cost of insurance for a diesel truck", id: "diesel_ins_yr_price", defaultValue: 15000, unit: "per year" },
      { label: "Electric insurance cost", description: "Cost of insurance for an electric truck", id: "elec_ins_yr_price", defaultValue: 15000, unit: "per year" },
    ],
  },
  dieselTruckCost: {
    category: "Diesel Truck Cost",
    inputs: [
      { label: "Heavy duty diesel truck MSRP", description: "Purchase price of a heavy duty diesel truck", id: "heavy_duty_diesel_price", defaultValue: 150000 },
      { label: "Heavy duty diesel truck MPG", description: "Fuel efficiency of diesel trucks", id: "heavy_duty_diesel_mpg", defaultValue: 6.5, unit: "mpg" },
      { label: "Medium duty diesel truck MSRP", description: "Purchase price of a medium duty diesel truck", id: "medium_duty_diesel_price", defaultValue: 80000 },
      { label: "Medium duty diesel truck MPG", description: "Fuel efficiency of diesel trucks", id: "medium_duty_diesel_mpg", defaultValue: 10, unit: "mpg" },
      { label: "Light duty diesel truck MSRP", description: "Purchase price of a light duty diesel truck", id: "light_duty_diesel_price", defaultValue: 65000 },
      { label: "Light duty diesel truck MPG", description: "Fuel efficiency of diesel trucks", id: "light_duty_diesel_mpg", defaultValue: 24, unit: "mpg" },
    ],
  },
  truckLife: {
    category: "Truck Operational Life",
    inputs: [
      { label: "Electric truck life", description: "Expected operational life of electric trucks", id: "electric_life", defaultValue: 12, unit: "years" },
      { label: "Diesel truck life", description: "Expected operational life of diesel trucks", id: "diesel_life", defaultValue: 12, unit: "years" },
    ],
  },
  depotCharging: {
    category: "Depot Charging",
    inputs: [
      { label: "Demand charge", description: "Monthly power demand charge", id: "demand_charge", defaultValue: 0.0, unit: "$/kW" },
    ],
  },
  publicCharging: {
    category: "Public Charging",
    inputs: [
      { label: "Public charging cost", description: "Additional cost at public charging stations", id: "pub_charge_cost", defaultValue: 0.2, unit: "$/kWh" },
    ],
  },
  diesel: {
    category: "Diesel",
    inputs: [
      { label: "Fuel cost", description: "Cost per gallon of diesel", id: "fuel_cost", defaultValue: 7.501, unit: "$/gal" },
    ],
  },
  taxRates: {
    category: "Tax Rates",
    inputs: [
      { label: "EV tax rate", description: "Tax rate for electric vehicles", id: "ev_tax_rate", defaultValue: 0, unit: "%" },
      { label: "Diesel tax rate", description: "Tax rate for diesel vehicles", id: "diesel_tax_rate", defaultValue: 0, unit: "%" },
    ],
  },
};

// Define tab configuration
const parameterTabs = [
  // { id: "penalties", label: "Penalties" }
];

// Initialize the parameters
export const initializeDefaultParameters = () => {
  const defaults = {};
  Object.values(parameterGroups).forEach((group) => {
    group.inputs.forEach((input) => {
      defaults[input.id] = input.defaultValue ?? 0;
    });
  });
  return defaults;
};

const OtherParameters = ({ otherParameters, setOtherParameters }) => {
  const [activeTab, setActiveTab] = useState(null);

  // Step guide integration
  const guideContent = getStepGuide('parameters');
  const {
    isOpen: isGuideOpen,
    closeGuide,
    dontShowAgain,
    onDontShowAgain
  } = useStepGuide('parameters', guideContent, false);

  const handleInputChange = (id, value) => {
    setOtherParameters(prev => ({ ...prev, [id]: value }));
  };

  const formatCurrency = (value) => value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const formatPercentage = (value) => {
    const num = Number(value);
    return isNaN(num) ? "0.00" : num.toFixed(2);
  };

  const renderContent = (tabId) => {
    const descriptions = {
      // penalties: "Operating in certain areas may incur additional penalties if you are not using zero-emission vehicles. Enter the estimated daily costs per truck for this, if applicable to your operational area."
    };
    const group = parameterGroups[tabId];
    const formatter = formatCurrency;

    return (
      <div>
        <p className="text-gray-600 mb-4">{descriptions[tabId]}</p>
        <ParameterTable
          parameters={group.inputs}
          values={otherParameters}
          onValueChange={handleInputChange}
          formatValue={formatter}
        />
      </div>
    );
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <TabPanel
        tabs={parameterTabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      >
        {/* No tabs currently active */}
      </TabPanel>

      {/* Step Guide Modal */}
      {guideContent && (
        <StepGuideModal
          isOpen={isGuideOpen}
          onClose={closeGuide}
          title={guideContent.title}
          description={guideContent.description}
          bullets={guideContent.bullets}
          dontShowAgain={dontShowAgain}
          onDontShowAgain={onDontShowAgain}
        />
      )}
    </div>
  );
};

export default OtherParameters; 